import * as Turf from '@turf/turf'
import Clone from 'lodash-es/clone';
import DayJS from 'dayjs';
import ReactDOMServer from 'react-dom/server';
import { FEATURE_COLOR, ONE_METER_TO_FEET, ONE_SQ_METER_TO_ACRE, DEFAULT_FEATURE_COLOR, WEIGHT_OPTIONS, ICON_LIST, ICON_DISPLAY_MAP } from './constants';
import Storage from './storage';
import typeIcons from '../assets/featureType';

export const checkKeyWords = (content, words) => {
  return content.toLowerCase().split(/[ -_]/g).find(c => words.includes(c))
}

export const exportSVG = (svg) => {
  const buff = new Buffer(svg);
  const base64data = buff.toString('base64');

  return Clone(`data:image/svg+xml;base64,${base64data}`);
};

export const isNumeric = (str) => {
  if (typeof str == 'number') return true;
  if (typeof str != 'string') return false; // we only process strings!  
  return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str)); // ...and ensure strings of whitespace fail
};

export function arrayBufferToString(buffer, utf8 = true) {
  var bufView;
  if (utf8) {
    bufView = new Uint8Array(buffer);
  } else {
    bufView = new Uint16Array(buffer);
  }
  var length = bufView.length;
  var result = '';
  var addition = Math.pow(2, 16) - 1;
  for (var i = 0; i < length; i += addition) {
    if (i + addition > length) {
      addition = length - i;
    }
    result += String.fromCharCode.apply(null, bufView.subarray(i, i + addition));
  }
  return result;
}

export function toTitleCase(string) {
  if (!string || string == null) return '';

  return string
    .split(' ')
    .map(([firstChar, ...rest]) => firstChar.toUpperCase() + rest.join('').toLowerCase())
    .join(' ');
}

export function svgToDataURI(element) {
  let staticMarkup = ReactDOMServer.renderToStaticMarkup(element);
  return exportSVG(staticMarkup);
}

export const cleanName = (name) => {
  return name.replace(/[^a-zA-Z0-9]/g, ' ');
};

export const arrayEq = (a, b) => {
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

export const objectEq = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b)
};

export const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)

export const slugify = (str) => {
  if (!str || typeof str != 'string') return '';
  return str.replace(/ /g, '_').toLowerCase();
};
export const kebabify = (str) => {
  if (!str || typeof str != 'string') return '';
  return str.replace(/ /g, '-').toLowerCase();
};

export const getColorName = (inRGBA) => {
  let colorName = DEFAULT_FEATURE_COLOR;
  Object.keys(FEATURE_COLOR).forEach((color) => {
    let compRGBA = FEATURE_COLOR[color].rgba;
    if (arrayEq(inRGBA, compRGBA)) colorName = color;
  });
  return colorName;
};

export const getColor = (feature, opacity = 1) => {
  let color = FEATURE_COLOR.Orange.rgba;

  if (feature && feature.properties && feature.properties.color) {
    color = feature.properties.color;
  }

  return `rgba(${color[0]},${color[1]},${color[2]},${opacity})`;
};

export const getIconSVGNameFromSlug = (iconSlug) => {
  return ICON_DISPLAY_MAP[iconSlug].replace(/ /g, '');
};

export const stripXML = (str) => {
  return str.replace(/<\/?[^>]+(>|$)/g, '');
};

export const delay = (ms) => new Promise(res => setTimeout(res, ms));

export const prettyNumber = (num, precision = 2) => {
  if (num == null) return '--';
  return num.toLocaleString('en-us', { minimumFractionDigits: precision, maximumFractionDigits: precision });
};

export const metersToFeet = (metersValue) => {
  return metersValue * ONE_METER_TO_FEET;
};

export const metersToMiles = (metersValue) => {
  return (metersToFeet(metersValue) / 5280.0).toFixed(2);
};

export const squareMetersToAcres = (sqMetersValue) => {
  return sqMetersValue * ONE_SQ_METER_TO_ACRE;
};

export const asTime = (seconds) => {
  const toTwoDigits = (num) => num < 10 ? '0' + num : num;

  let hours = 0;
  let minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 60) {
    hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  return `${toTwoDigits(hours)}:${toTwoDigits(minutes)}:${toTwoDigits(remainingSeconds)}`;
};

export const findWeightIcon = (width) => {
  for (let i = 0; i < WEIGHT_OPTIONS.length; i++) {
    if (WEIGHT_OPTIONS[i].value === width) {
      return WEIGHT_OPTIONS[i].image;
    }
  }
};

export const findWeightLabel = (width) => {
  for (let i = 0; i < WEIGHT_OPTIONS.length; i++) {
    if (WEIGHT_OPTIONS[i].value === width) {
      return WEIGHT_OPTIONS[i].text;
    }
  }
};

export const findTypeIcon = (name) => {
  for (let i = 0; i < ICON_LIST.length; i++) {
    if (ICON_LIST[i].key === name) {
      return typeIcons[ICON_LIST[i].display.replace(/ /g, '')];
    }
  }
};

export const findTypeLabel = (name) => {
  for (let i = 0; i < ICON_LIST.length; i++) {
    if (ICON_LIST[i].key === name) {
      return ICON_LIST[i].display;
    }
  }
};

export const getUser = () => {
  const currentUser = Storage.get('currentUser');
  if (!currentUser) {
    return null;
  }

  return currentUser;
};

export const stringToHash = (string) => {
  let hash = 0;
  if (string.length == 0) return hash;

  for (let i = 0; i < string.length; i++) {
    let char = string.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return hash;
};

export const formatDate = (dateString, formatString = 'MMM D, YYYY h:mm A') => {
  let dateValue = dateString;
  if (typeof dateValue === 'number') {
    if ([9, 10].includes(dateValue.toString().length)) {
      dateValue = dateValue * 1000;
    }
  }
  return DayJS(dateValue).format(formatString);
};

export const splitLastChar = (str) => {
  return [str.substr(0, str.length - 1), str.slice(-1)];
};

export const sortList = (list, key, sortBy, ascending = true) => {
  if (list?.length > 0 && key) {
    if (sortBy === 'number') {
      const extractNumber = value => {
          if (typeof value !== 'string') {
            value = `${value}`
          }
          if (value.startsWith('<')) {
            return -Infinity
          }
          const match = value.match(/[\d,.]+/)
          return match ? parseFloat(match[0].replace(/,/g, '')) : -Infinity
        }
        if (ascending) {
          list.sort((a,b) => {
            const num1 = extractNumber(a[key])
            const num2 = extractNumber(b[key])
            return num1 < num2 ? -1 : num1 > num2 ? 1 : 0
          })
        } else {
          list.sort((a,b) => {
            const num1 = extractNumber(a[key])
            const num2 = extractNumber(b[key])
            return num2 < num1 ? -1 : num2 > num1 ? 1 : 0
          })
        }
        return list
    } else {
      if (ascending) {
        list.sort((a, b) => a[key].localeCompare(b[key]))
      } else {
        list.sort((a, b) => b[key].localeCompare(a[key]))
      }
      return list
    }
  } else {
    return list
  }
}
export const setBoundingBox = (point, distance = 3) => {
  let boundingBox = []
  const turfPoint = Turf.point(point)
  const coordinatesBuffer = Turf.buffer(turfPoint, distance, { units: 'meters' });
  const turfBoundingBox = Turf.bbox(coordinatesBuffer)
  const [xmax, ymin, xmin, ymax] = turfBoundingBox
  if (xmax && ymin && xmin && ymax) {
    const boundingBoxValues = [{ type: 'min', coordinates: [xmin, ymin] },{ type: 'max', coordinates: [xmax, ymax] }]
    const turfFeature = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point' }
    }
    boundingBoxValues.forEach(bbv => {
      turfFeature.geometry.coordinates = bbv.coordinates
      const { geometry } = Turf.toMercator(turfFeature)
      if (geometry && geometry.coordinates) {
        if (bbv.type === 'min') {
          boundingBox = {
            ...boundingBox,
            xmin: geometry.coordinates[0],
            ymin: geometry.coordinates[1],
          }
        } else if (bbv.type === 'max') {
          boundingBox = {
            ...boundingBox,
            xmax: geometry.coordinates[0],
            ymax: geometry.coordinates[1],
          }
        }
      }
    })
  }

  return boundingBox
}

export const getCurrentSeason = (date) => {
  const seasons = ['Winter', 'Spring', 'Summer', 'Fall']
  const cycle = Math.floor((date.getMonth() / 12 * 4)) % 4
  return seasons[cycle]
}

export const reverseArray = (array) => {
  const newArray = []
  let ai = array.length - 1
  for (ai; ai >= 0; ai--) {
    newArray.push(array[ai])
  }
  return newArray
}

export const setScrollPosition = (id, block = 'center', inline = 'nearest') => {
  const currentElement = document.getElementById(id)
  if (currentElement) {
    currentElement.scrollIntoView({ block, inline })
  }
}

export const scrollBarCheck = (id) => {
  const currentElement = document.getElementById(id)
  return currentElement?.scrollHeight > currentElement?.clientHeight
}

export const getItemIndex = ({ items, key, reverse, value }) => {
  if (!items.length) return -1

  if (reverse) {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i][key].includes(value)) {
        return i
      }
    }
  } else {
    for (let i = 0; i < items.length; i++) {
      if (items[i][key].includes(value)) {
        return i
      }
    }
  }

  return -1
}

export const getItemsUpToValue = ({ items, key, reverse, value }) => {
  const result = []
  if (!items.length) return result

  if (reverse) {
    for (let i = items.length - 1; i >= 0; i--) {
      if (!items[i][key].includes(value)) {
        result.push(items[i])
      } else {
        break
      }
    }
  } else {
    for (let i = 0; i < items.length; i++) {
      if (!items[i][key].includes(value)) {
        result.push(items[i])
      } else {
        break
      }
    }
  }

  return result
}
